import {ChangeDetectionStrategy, Component, computed, Inject, input} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {DokumentBakgrunnDirektiv, DokumentFilnavnDirektiv, DokumentHandlingDirektiv, DokumentKomponent, DokumentMetainformasjonDirektiv} from "web-ng2/delt/komponenter/dokument/dokument";
import {MatTooltip} from "@angular/material/tooltip";
import {DokumentForsidebildeKomponent} from "../dokument-forsidebilde/dokument-forsidebilde";
import {DokumentFormatIkonKomponent} from "../dokument-format-ikon/dokument-format-ikon";
import {MatIcon} from "@angular/material/icon";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {MatMenuTrigger} from "@angular/material/menu";
import {TranslateModule} from "@ngx-translate/core";
import {MatIconButton} from "@angular/material/button";
import {DokumentMenyKomponent} from "web-ng2/moduler/dokument/komponenter/dokument-meny/dokument-meny";
import {DokumentInlinePdfviserKomponent} from "web-ng2/moduler/dokument/komponenter/dokument-inline-pdfviser/dokument-inline-pdfviser";
import {animate, AnimationEvent, query, style, transition, trigger} from "@angular/animations";
import {DokumentvisningKomponentAvhengigheter} from "web-ng2/moduler/dokument/komponenter/dokumentvisning/DokumentvisningKomponentAvhengigheter";
import {DelegerteDokumentvisningOperasjoner, DelegerteDokumentvisningOperasjoner_DI_TOKEN, DokumentvisningBasisKomponent} from "web-ng2/moduler/dokument/komponenter/dokumentvisning/DokumentvisningBasisKomponent";
import {ZonedDateTimeSomPlainDatePipe} from "felles-ng2/pipes/ZonedDateTimeSomPlainDatePipe";
import {FormaterPlainDatePipe} from "felles-ng2/pipes/FormaterPlainDatePipe";
import {Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {DokumentInformasjonTjeneste} from "felles-ng2/tjenester/DokumentInformasjonTjeneste";
import {ÅpneDokumentTjeneste} from "web-ng2/moduler/dokument/tjenester/ÅpneDokumentTjeneste";
import {DokumentSigneringTjeneste} from "web-ng2/moduler/dokument/tjenester/DokumentSigneringTjeneste";
import {KlikkMedStopPropagationDirektiv} from "web-ng2/delt/direktiver/klikkMedStopPropagation/klikkMedStopPropagation";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";

const debug = initDebugger('DokumentDetaljerKomponent');

export type ÅpneValg = { inline: boolean };
type Forsidetype = 'infisert' | 'virussjekk' | 'forside' | 'ikon';

/**
 * Viser dokument i kort-format.
 * Merk at kallende komponent forventes å ha tilgjengeliggjort:
 * - {@link DelegerteDokumentvisningOperasjoner} under tokenet {@link DelegerteDokumentvisningOperasjoner_DI_TOKEN}, som konsumeres av {@link DokumentKortvisningKomponent}
 * - {@link DelegerteDokumentMenyOperasjoner} under tokenet {@link DelegerteDokumentMenyOperasjoner_DI_TOKEN}, som konsumeres av {@link DokumentMenyKomponent}
 */
@Component({
    selector: 'dokument-kortvisning',
    templateUrl: 'dokument-kortvisning.html',
    styleUrl: 'dokument-kortvisning.scss',
    host: {
        //følgende kunne ha ligget i DokumentvisningBasisKomponent men det gir feil av typen `NG0100: ExpressionChangedAfterItHasBeenCheckedError`
        '(dblclick)': 'aapneInline()',
        '[class.viser-inline]': 'viserInline()',
        '[attr.id]': '`dok-${dokumentversjon().dokumentId}`',
        'data-testid': 'dokument',
        //DokumentKortvisningKomponent spesifikke host-attributter
        '[@skaler]': '{value: viserInline() ? "ekspandert":"normal", params: {breddeNormal:dokumentBreddeNormal(), hoeydeNormal:dokumentHoeydeNormal(), breddeEkspandert:dokumentBreddeEkspandert, hoeydeEkspandert:dokumentHoeydeEkspandert}}',
        '(@skaler.done)': 'inlineVisningFerdigAnimert($event)',
        '[style.--dokument-bredde-normal]': 'dokumentBreddeNormal()',
        '[style.--dokument-høyde-normal]': 'dokumentHoeydeNormal()',
        '[style.--dokument-bredde-ekspandert]': 'dokumentBreddeEkspandert',
        '[style.--dokument-høyde-ekspandert]': 'dokumentHoeydeEkspandert',
    },
    animations: [skalerDokumentVedInlineVisning()],
    imports: [
        DokumentKomponent,
        DokumentBakgrunnDirektiv,
        DokumentHandlingDirektiv,
        DokumentFilnavnDirektiv,
        DokumentMetainformasjonDirektiv,
        MatTooltip,
        DokumentForsidebildeKomponent,
        DokumentFormatIkonKomponent,
        MatIcon,
        TranslateModule,
        MatIconButton,
        MatMenuTrigger,
        DokumentMenyKomponent,
        DokumentInlinePdfviserKomponent,
        ZonedDateTimeSomPlainDatePipe,
        FormaterPlainDatePipe,
        KlikkMedStopPropagationDirektiv

    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DokumentKortvisningKomponent extends DokumentvisningBasisKomponent {

    static readonly standardBredde = '160px';
    static readonly standardHøyde = '200px';

    /** Bredden til dokumentet når inline-visning ER aktivert. */
    readonly dokumentBreddeEkspandert = '100%';
    //dokumentHoeydeEkspandert ligger i DokumentvisningBasisKomponent

    /** Bredden til dokumentet når ikke inline-visning er aktivert. */
    dokumentBreddeNormal = input(DokumentKortvisningKomponent.standardBredde, {alias: 'bredde'});
    /** Høyden til dokumentet når ikke inline-visning er aktivert.
     * Bør være minimum 140px (som er nødvendig høyde når filnavn vises over 4 linjer, som er maks for filnavn) */
    dokumentHoeydeNormal = input(DokumentKortvisningKomponent.standardHøyde, {alias: 'hoeyde'});

    forsidetype = computed(() => this.kalkulerForsidetype());

    constructor(avhengigheter: DokumentvisningKomponentAvhengigheter,
                @Inject(DelegerteDokumentvisningOperasjoner_DI_TOKEN) delegerteOperasjoner: DelegerteDokumentvisningOperasjoner,
                dokumentInformasjon: DokumentInformasjonTjeneste,
                dokumentTjeneste: ÅpneDokumentTjeneste,
                dokumentSigneringTjeneste: DokumentSigneringTjeneste,
                oversettelser: Oversettelser,
                navigasjon: Navigasjon) {
        super(avhengigheter, delegerteOperasjoner, dokumentInformasjon, dokumentTjeneste, dokumentSigneringTjeneste, oversettelser, navigasjon);
    }

    //DokumentvisningBasisKomponent implementasjon

    protected override erVisInlineAnimasjonFerdig(hendelse: AnimationEvent): boolean {
        return hendelse.toState == 'ekspandert';
    }

    //private hjelpemetoder

    private kalkulerForsidetype(): Forsidetype {
        if (this.informasjon().infisert) return 'infisert';
        if (this.informasjon().virussjekkPaagaar) return 'virussjekk';
        if (erIkkeNil(this.dokumentversjon().forsideBildeId) /*&& this.visForsideBilde !== false*/) return 'forside';
        return 'ikon';
    }
}


function skalerDokumentVedInlineVisning() {
    return trigger('skaler', [
        transition('* => ekspandert', [
              //Sørger fot at <dokument-kortvisning> opptar full tilgjengelig bredde før animering starter,
              //dvs at dersom det finnes andre dokumenter før/etter vil det animerte dokumentet hoppe ned til egen rad før animeringen starter,
              //dette for å unngå 'hopp' effekten dersom dokumentet først havner på egen rad underveis i animeringen når det bredde
              //blir større enn det er plass til på eksisterende rad
              style({width: '100%'}),
              //Sørger for at dokument-inline-pdfviser ikke blir synlig før animasjon av dokument er ferdig,
              //dermed er det dokument med forsidebilde som skaleres
              query('dokument-inline-pdfviser', [
                  style({display: 'none'}) //denne forsvinner når animasjon er ferdig
              ]),
              query('.bakgrunn-kontainer-normal', [
                  style({display: 'contents'}) //denne forsvinner når animasjon er ferdig, og 'display: none' satt via .viser-inline i scss får effekt
              ]),
              //.viser-inline{width:100%} sørger for at dokumentet kommer på ny rad før det ekspanderer til endelig bredde
              //skaler dokument til width:100%
              style({width: '100%'}),
              query('dokument', [
                  style({width: '{{breddeNormal}}', height: '{{hoeydeNormal}}', overflow: 'hidden'}),
                  animate('0.2s ease-in', style({width: '{{breddeEkspandert}}', height: '{{hoeydeEkspandert}}'}))
              ])
              //kun fadet pdf-viser inn mens dokument skalerer opp - men med så kort animasjon som 0.2s gir dette ingen særlig synlige effekt
              /*group([
                  style({width: '100%'}),  query('dokument', [
                      style({width: '{{breddeNormal}}', height: '{{hoeydeNormal}}', overflow: 'hidden'}),
                      animate('0.2s ease-in', style({width: '{{breddeEkspandert}}', height: '{{hoeydeEkspandert}}'}))
                  ]),
                  query('.bakgrunn-kontainer-ekspandert', [
                      style({position: 'absolute', width: '100%', height: '100%',opacity: '0.1', display: 'block'}),
                      animate('0.2s ease-in', style({opacity:'1'}))
                  ])
              ])*/
          ],
          skalerDokumentVedInlineVisningParametre()),

        transition('ekspandert => normal', [
              style({width: '100%'}),
              query('dokument', [
                  style({width: '{{breddeEkspandert}}', height: '{{hoeydeEkspandert}}', overflow: 'hidden'}),
                  animate('0.2s ease-in', style({width: '{{breddeNormal}}', height: '{{hoeydeNormal}}'}))
              ])
          ],
          skalerDokumentVedInlineVisningParametre())
    ])
}

function skalerDokumentVedInlineVisningParametre() {
    return {params: {breddeNormal: '100px', hoeydeNormal: '100px', breddeEkspandert: '100%', hoeydeEkspandert: '600px'}}
}
